/**
 * wfState — the ONE definition of waveform serveability on the frontend.
 *
 * Mirrors backend `_wf_state_sql` (backend/routers/machines.py), which renders the
 * same three-state rule into /machines/{id}/trend and /machines/{id}/peakvue-trend.
 * Every trend chart on every page reads this module; nothing re-derives the states
 * or re-picks the colours locally. Two copies of a rule is how the last drift
 * started — MachineDetail and PointDetail had diverged on exactly this.
 *
 *   fast        TWF in Spaces            -> viewer opens in <100ms
 *   slow        no stored TWF, but /measurements/waveform's EI-Analytic fallback
 *               resolves it (~2.4s, verified). STILL CLICKABLE — it works, so
 *               label it rather than hide it.
 *   unserveable EI confirmed no waveform -> not plotted, not clickable.
 *
 * States are computed LIVE server-side, never cached: the recovery drain is landing
 * TWFs for exactly the 'slow' population, so points upgrade slow -> fast on their
 * own with no cache to invalidate.
 */

export type WfState = 'fast' | 'slow' | 'unserveable';

export const WF_SLOW_NOTE = 'waveform not yet imported — slower to open';

/** Muted grey for 'slow' so it reads as degraded-but-usable, not as a second series. */
const WF_COLOUR: Record<WfState, string> = {
  fast:        '#06b6d4',
  slow:        '#94a3b8',
  unserveable: '#94a3b8',
};

/**
 * Read wf_state off a trend/peakvue row.
 *
 * Absent field -> 'fast'. That is the honest default: every endpoint that serves
 * waveform-backed points now returns wf_state, so a missing one means a row type
 * that was never gated (and whose click has always worked). It is NOT a silent
 * fallback for the retired `wf_serveable` shim — that field is gone from the
 * backend; do not reintroduce a second field meaning an overlapping thing.
 */
export function resolveWfState(row: { wf_state?: string | null }): WfState {
  const s = row?.wf_state;
  return s === 'slow' || s === 'unserveable' || s === 'fast' ? s : 'fast';
}

/** Plottable == clickable. 'slow' is both; only 'unserveable' is neither. */
export function isPlottable(state: WfState): boolean {
  return state !== 'unserveable';
}

export function wfMarkerColour(state: WfState | undefined): string {
  return WF_COLOUR[state ?? 'fast'] ?? WF_COLOUR.fast;
}

/** Dashed for 'slow' so it is distinguishable without relying on colour alone. */
export function wfMarkerDash(state: WfState | undefined): string | undefined {
  return state === 'slow' ? '4 3' : undefined;
}

/** 'YYYY/MM/DD HH:MM:SS' -> "Apr 21 9:26 PM — click to load" (or the slow note). */
export function fmtWfLabel(measured_at: string, state: WfState = 'fast'): string {
  const suffix = state === 'slow' ? WF_SLOW_NOTE : 'click to load';
  const [dp, tp = '00:00:00'] = (measured_at || '').split(' ');
  const [, mo, dy] = dp.split('/');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [hh, mm] = tp.split(':');
  let h = parseInt(hh, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${months[parseInt(mo, 10) - 1]} ${parseInt(dy, 10)} ${h}:${mm} ${ampm} — ${suffix}`;
}
